import React, { useEffect, useRef } from 'react';
import { JcSize } from '../utils/JcUtils';
import { JcIconButton } from './button/JcIconButton';
import { JcPopup } from './dialog/JcPopup';
import { JcGalleryImage } from './JcGallery';
import { JcBuiltInIcon } from './icon/JcBuiltInIcons';
import { JcIconType } from './icon/JcIcon';

export interface JcLightboxProps {
    readonly images: ReadonlyArray<JcGalleryImage>;
    /** The open photo, or `null` when closed. */
    readonly index: number | null;
    readonly onIndexChange: (index: number) => void;
    readonly onClose: () => void;
    /** Accessible name for the viewer, e.g. the gallery's title. */
    readonly ariaLabel: string;
}

const SWIPE_THRESHOLD_PX = 50;

/**
 * A full-screen photo viewer on `JcPopup` (focus trap, Escape, focus restored
 * to the opening thumbnail). Previous/next wrap around and are reachable by
 * buttons, ←/→ keys, and horizontal swipe; neighbours are preloaded.
 */
export const JcLightbox = (props: JcLightboxProps): React.ReactElement | null => {
    const { images, index, onIndexChange, onClose, ariaLabel } = props;
    const swipeStart = useRef<{ x: number; y: number } | null>(null);
    const count = images.length;
    const open = index !== null && index >= 0 && index < count;

    const step = (delta: number) => {
        if (index !== null) {
            onIndexChange((index + delta + count) % count);
        }
    };
    // The key listener outlives renders, so it reads the latest `step` via a ref.
    const stepRef = useRef(step);
    stepRef.current = step;

    useEffect(() => {
        if (!open || count < 2) {
            return;
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                stepRef.current(-1);
            } else if (e.key === 'ArrowRight') {
                stepRef.current(1);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, count]);

    useEffect(() => {
        if (index === null || count < 2) {
            return;
        }
        [index - 1, index + 1].forEach(neighbour => {
            new Image().src = images[(neighbour + count) % count].fullURL;
        });
    }, [images, index, count]);

    if (!open || index === null) {
        return null;
    }
    const image = images[index];

    const handlePointerDown = (e: React.PointerEvent) => {
        swipeStart.current = { x: e.clientX, y: e.clientY };
    };
    const handlePointerUp = (e: React.PointerEvent) => {
        const start = swipeStart.current;
        swipeStart.current = null;
        if (start === null || count < 2) {
            return;
        }
        const dx = e.clientX - start.x;
        if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(e.clientY - start.y)) {
            step(dx < 0 ? 1 : -1);
        }
    };

    return (
        <JcPopup open onClose={onClose} className="jc-lightbox" ariaLabel={ariaLabel}>
            <div className="jc-lightbox-top-bar">
                <JcIconButton
                    size={JcSize.MEDIUM}
                    ariaLabel="Close"
                    iconProps={{ type: JcIconType.BUILT_IN, icon: JcBuiltInIcon.X_MARK }}
                    onClick={onClose}
                />
                <span className="jc-lightbox-counter" aria-live="polite">
                    {index + 1} / {count}
                </span>
            </div>
            <div
                className="jc-lightbox-stage"
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onClick={e => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                {count > 1 && (
                    <JcIconButton
                        className="jc-lightbox-nav jc-lightbox-nav-previous"
                        size={JcSize.MEDIUM}
                        ariaLabel="Previous photo"
                        iconProps={{ type: JcIconType.BUILT_IN, icon: JcBuiltInIcon.CHEVRON_LEFT }}
                        onClick={() => step(-1)}
                    />
                )}
                <figure className="jc-lightbox-figure">
                    <img
                        key={image.id}
                        className="jc-lightbox-image"
                        src={image.fullURL}
                        alt={image.alt}
                        width={image.width}
                        height={image.height}
                        draggable={false}
                    />
                    {image.caption && <figcaption className="jc-lightbox-caption">{image.caption}</figcaption>}
                </figure>
                {count > 1 && (
                    <JcIconButton
                        className="jc-lightbox-nav jc-lightbox-nav-next"
                        size={JcSize.MEDIUM}
                        ariaLabel="Next photo"
                        iconProps={{ type: JcIconType.BUILT_IN, icon: JcBuiltInIcon.CHEVRON_RIGHT }}
                        onClick={() => step(1)}
                    />
                )}
            </div>
        </JcPopup>
    );
};
